import React, { useRef } from 'react';
import styled from 'styled-components';

import { CONTENTFUL_INPUT_TYPES } from '../../../shared/constants';
import { IMdBody } from '../../../types/shared';

import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';

import Colors from '../../../styles/colors';
import { ButtonLabel } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import InfoPopover from '../info-popover/InfoPopover';

interface Props {
  id: string;
  name: string;
  value?: string;
  label: string;
  onInvalidHandler?: (
    validity: ValidityState,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>
      | React.FormEvent<HTMLInputElement>,
    customError?: string,
    type?: string
  ) => void;
  isRequired?: boolean;
  isChecked?: boolean;
  error?: string;
  tooltip?: IMdBody | null;
}

const FormRadiobutton = ({
  id,
  name,
  value,
  label,
  onInvalidHandler,
  isRequired,
  isChecked,
  error,
  tooltip,
}: Props) => {
  const radioRef = useRef<HTMLInputElement>(null);

  const validate = (
    validity: ValidityState,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>
      | React.FormEvent<HTMLInputElement>
  ) => {
    if (onInvalidHandler)
      onInvalidHandler(
        validity,
        event,
        undefined,
        CONTENTFUL_INPUT_TYPES.radio
      );
  };

  return (
    <Container>
      <StyledLabel
        className="radiobutton"
        data-testid="form-radio"
        htmlFor={id}
      >
        <RadioWrapper>
          <StyledInput
            id={id}
            data-testid={`radio-${label}`.toLowerCase()}
            name={name}
            value={value}
            error={error}
            required={isRequired}
            checked={isChecked}
            type="radio"
            ref={radioRef}
            onChange={(event) => {
              if (radioRef?.current) {
                validate(radioRef.current?.validity, event);
              }
            }}
            onInvalid={(event) => {
              event.preventDefault();
              if (radioRef?.current) {
                validate(radioRef.current?.validity);
              }
            }}
          />
          {label}
        </RadioWrapper>
      </StyledLabel>
      {tooltip && (
        <InfoPopover label={label}>
          <ReactMarkdownExtended>
            {tooltip.childMarkdownRemark?.rawMarkdownBody}
          </ReactMarkdownExtended>
        </InfoPopover>
      )}
    </Container>
  );
};

export default FormRadiobutton;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 3px;
`;

const StyledInput = styled.input<{ error?: string }>`
  appearance: none;
  background-color: ${Colors.white};
  border: 1px solid ${Colors.abnNeutralsN100};
  border-radius: ${({ theme }) => theme.radiuses.XXL};
  display: grid;
  font: inherit;
  height: 1.15em;
  margin: 0;
  margin-right: ${Spacings.XS};
  padding: ${Spacings.XS};
  place-content: center;
  position: relative;
  width: 1.15em;

  &:before {
    border-radius: ${({ theme }) => theme.radiuses.XXL};
    box-shadow: inset 16px 16px ${({ theme }) => theme.colors.abnGreen200};
    content: '';
    height: 0.65em;
    transform: scale(0);
    transition: 0.1s transform ease-in-out;
    width: 0.65em;
  }

  &:checked {
    border: 1px solid ${({ theme }) => theme.colors.abnGreen200};
    background-color: ${Colors.white};

    &:before {
      transform: scale(1);
    }
  }
  &:not(:checked) {
    &:active {
      background-color: ${Colors.abnGreen65};
      border: 1px solid ${({ theme }) => theme.colors.abnGreen100};
    }
  }

  &:focus {
    &:after {
      border: 2px solid ${Colors.abnLightBlue};
      border-radius: ${({ theme }) => theme.radiuses.XXL};
      content: '';
      height: 1.4em;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 1.4em;
    }
  }
`;

const StyledLabel = styled.label`
  display: flex;
  font-family: ${({ theme }) => theme.fontRegular};
  ${ButtonLabel}
  justify-content: space-between;

  &:not(:active) {
    &:hover {
      cursor: pointer;

      input[type='radio'] {
        background-color: ${Colors.abnGreen55};
        border: 1px solid ${Colors.abnGreen75};
        cursor: pointer;

        &:before {
          box-shadow: inset 16px 16px ${({ theme }) => theme.colors.abnGreen300};
        }

        &:checked {
          background-color: ${Colors.white};
          border: 1px solid ${({ theme }) => theme.colors.abnGreen200};
        }
      }
    }
  }
`;

const RadioWrapper = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: row;
`;
